import * as React from 'react';
import { Typography, Grid } from '@mui/material';
import ProductCodeInput from './ProductCodeInput_NEW';
import { OrderProduct, RentalState, SetProduct, Shopper } from 'common/types';
import { updateActiveShopperId } from 'actions';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization/useTranslation';
import { getOrderProductReturnStatus, requiresProductCode } from 'common/modules/orders';

interface ProductCodeFieldProps {
	product: OrderProduct | SetProduct;
	parentProduct: OrderProduct | null;
	productId: string;
	shopper: Shopper;
	rentalState: RentalState;
	isSetProduct: boolean;
	setIndex?: number;
	updateActiveShopperId: (shopperId: string) => void;
}

export const ProductCodeField = (props: ProductCodeFieldProps) => {
	const { product, parentProduct, shopper, isSetProduct, setIndex, productId, rentalState } = props;
	const { t } = useTranslation();
	const { localFormat } = useShopFormat();

	const { returnedDate, fulfillmentDate, setRemovedFromParent, returnStatus } = (() => {
		let returnedDate = null;
		let fulfillmentDate = null;
		let setRemovedFromParent = undefined;

		if (isSetProduct) {
			const setProduct = product as SetProduct;

			returnedDate = setProduct.returnedDate;
			fulfillmentDate = parentProduct?.fulfillmentDate ?? null;
			setRemovedFromParent = setProduct.removedFromParent;
		} else {
			const orderProduct = product as OrderProduct;

			returnedDate = orderProduct.endDateReturned;
			fulfillmentDate = orderProduct.fulfillmentDate;
		}

		return {
			returnedDate,
			fulfillmentDate,
			setRemovedFromParent,
			returnStatus: getOrderProductReturnStatus({
				purchaseType: product.purchaseType,
				returnedDate,
				fulfillmentDate,
			}),
		};
	})();

	const renderProductCodeInputField = () => {
		if (requiresProductCode(product)) {
			const isDisabled = (() => {
				switch (rentalState) {
					case 'BOOKED': {
						return false;
					}
					case 'ACTIVE': {
						return returnStatus !== 'none';
					}
					case 'COMPLETED': {
						return true;
					}
					default:
						return true;
				}
			})();

			return (
				<ProductCodeInput
					productId={productId}
					shopper={shopper}
					orderProduct={product}
					parentProduct={parentProduct}
					setIndex={isSetProduct && setIndex != null ? setIndex : null}
					dataQA="codeInput"
					disabled={isDisabled}
				/>
			);
		}
		return null;
	};

	const renderInputField = () => {
		if (setRemovedFromParent) {
			return (
				<Typography variant={'caption'} sx={{ color: (theme) => theme.palette.action.focus }}>
					{t('shopperProducts.productRemovedFromSet', 'Product removed from set')}
				</Typography>
			);
		}

		return (
			<>
				{renderProductCodeInputField()}

				{!!returnedDate && !!fulfillmentDate ? (
					<Typography variant="caption">
						{t('shopperProducts.restocked', 'Restocked')}{' '}
						{localFormat(returnedDate, 'DD.MM., HH:mm')}
					</Typography>
				) : !!returnedDate ? (
					<Typography variant="caption">
						{t('shopperProducts.returned', 'Returned')} {localFormat(returnedDate, 'DD.MM., HH:mm')}
					</Typography>
				) : !!fulfillmentDate ? (
					<Typography variant="caption">
						{t('shopperProducts.fulfilled', 'Fulfilled')}{' '}
						{localFormat(fulfillmentDate, 'DD.MM., HH:mm')}
					</Typography>
				) : null}
			</>
		);
	};

	return (
		<Grid
			item
			xs={12}
			lg={6}
			onClick={(e) => {
				e.stopPropagation();
				updateActiveShopperId(shopper.id);
			}}
		>
			{renderInputField()}
		</Grid>
	);
};

export default ProductCodeField;
